import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import {
  useLazyGetUserProfileQuery,
  useLazyGetUserLoginInfoPreviewQuery,
  useSendUserLoginInfoMutation,
  useResetUserCredentialsMutation,
  useSendUserCredentialsMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

const AVATAR_COLORS = ["#2f3891", "#0ea5e9", "#16a34a", "#d97706", "#7c3aed", "#dc2626"];
const avatarColor = (name: string) => AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length];
const initials = (name: string) =>
  (name || "").split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();

const UserProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [fetchProfile, { data: profileData, isFetching: profileLoading, isUninitialized }] = useLazyGetUserProfileQuery();
  const [fetchLoginInfoPreview, { isFetching: previewLoading }] = useLazyGetUserLoginInfoPreviewQuery();
  const [sendLoginInfo, { isLoading: sendingInfo }] = useSendUserLoginInfoMutation();
  const [resetCredentials, { isLoading: resetting }] = useResetUserCredentialsMutation();
  const [sendCredentials, { isLoading: sending }] = useSendUserCredentialsMutation();

  const [loginInfoDialog, setLoginInfoDialog] = useState<{
    userId: number;
    userName: string;
    toEmail: string;
    subject: string;
    message: string;
  } | null>(null);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ id: number; email: string; password: string } | null>(null);

  useEffect(() => {
    if (id) fetchProfile(Number(id));
  }, [id, fetchProfile]);

  console.log("profileData", profileData);
  const p: any = (profileData as any)?.data;

  const openLoginInfoDialog = async () => {
    if (!p?.email) {
      showErrorMessage("User has no email address.");
      return;
    }
    try {
      const res: any = await fetchLoginInfoPreview(p.id).unwrap();
      const preview: any = res?.data ?? {};
      setLoginInfoDialog({
        userId: p.id,
        userName: p.full_name || p.username,
        toEmail: preview.to_email ?? p.email ?? "",
        subject: preview.subject ?? "",
        message: preview.message ?? "",
      });
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to load email preview");
    }
  };

  const submitLoginInfo = async () => {
    if (!loginInfoDialog) return;
    if (!loginInfoDialog.toEmail.trim()) {
      showErrorMessage("Recipient email is required.");
      return;
    }
    try {
      await sendLoginInfo({
        id: loginInfoDialog.userId,
        to_email: loginInfoDialog.toEmail.trim(),
        subject: loginInfoDialog.subject,
        message: loginInfoDialog.message,
      }).unwrap();
      showMessage(`Login info sent to ${loginInfoDialog.toEmail.trim()}`, "success");
      setLoginInfoDialog(null);
      if (id) fetchProfile(Number(id));
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to send login info");
    }
  };

  const handleResendCredentials = async () => {
    if (!p) return;
    try {
      const res: any = await resetCredentials({ id: p.id, send_email: true }).unwrap();
      const created = res?.data;
      setResendConfirmOpen(false);
      if (created?.id && created?.temp_password) {
        setSuccessInfo({ id: created.id, email: created.email, password: created.temp_password });
      }
      if (res?.mail_error) {
        showErrorMessage(res.mail_error);
      } else {
        showMessage(res?.message || "Password reset and credentials emailed", "success");
      }
      if (id) fetchProfile(Number(id));
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to reset credentials");
    }
  };

  const handleCopyPassword = async () => {
    if (!successInfo) return;
    try {
      await navigator.clipboard.writeText(successInfo.password);
      showMessage("Password copied to clipboard", "success");
    } catch {
      showErrorMessage("Unable to copy password");
    }
  };

  const handleSendCredentials = async () => {
    if (!successInfo) return;
    try {
      await sendCredentials({ id: successInfo.id, password: successInfo.password }).unwrap();
      showMessage("Credentials sent to " + successInfo.email, "success");
      setSuccessInfo(null);
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to send credentials");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate("/user-management/users")}
            sx={{ bgcolor: "#f1f5f9", color: "#475569", "&:hover": { bgcolor: "#e2e8f0" } }}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Box>
            <Typography fontWeight={700} fontSize={18} color="#0f172a">Employee Profile</Typography>
            <Typography fontSize={12} color="#94a3b8">Profile details and activity history</Typography>
          </Box>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ height: 4, bgcolor: "#2f3891" }} />
        <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
         {isUninitialized || profileLoading || !p ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#2f3891" }} />
            </Box>
          ) : (
            <>
              <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 2, display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar sx={{
                  width: 52, height: 52, fontSize: 18, fontWeight: 700,
                  bgcolor: avatarColor(p.full_name || ""), color: "#fff",
                }}>
                  {initials(p.full_name || p.username || "?")}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} fontSize={15} color="#0f172a">{p.full_name}</Typography>
                  <Typography fontSize={12} color="#64748b">Login: {p.username}</Typography>
                  {p.role_name && (
                    <Chip label={p.role_name} size="small"
                      sx={{ mt: 0.5, bgcolor: "#eef2ff", color: "#2f3891", fontWeight: 600, fontSize: 11 }} />
                  )}
                </Box>
                {p.email && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />}
                      disabled={sendingInfo || previewLoading}
                      onClick={openLoginInfoDialog}
                      sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#bbf7d0", color: "#16a34a" }}>
                      Send Login Info
                    </Button>
                    <Button variant="outlined" startIcon={<AutoFixHighOutlinedIcon />}
                      onClick={() => setResendConfirmOpen(true)}
                      sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#fed7aa", color: "#c2410c" }}>
                      Reset Password
                    </Button>
                  </Box>
                )}
              </Paper>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <Box>
                  <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5}>EMAIL</Typography>
                  <Typography fontSize={13} color="#0f172a" mt={0.3}>{p.email || "—"}</Typography>
                </Box>
                <Box>
                  <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5}>PHONE</Typography>
                  <Typography fontSize={13} color="#0f172a" mt={0.3}>{p.phone || "—"}</Typography>
                </Box>
                <Box>
                  <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5}>CREATED</Typography>
                  <Typography fontSize={13} color="#0f172a" mt={0.3}>
                    {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5}>LAST UPDATE</Typography>
                  <Typography fontSize={13} color="#0f172a" mt={0.3}>
                    {p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                  <HistoryIcon sx={{ fontSize: 18, color: "#475569" }} />
                  <Typography fontWeight={700} fontSize={13} color="#475569">Activity</Typography>
                </Box>
                {(p.logs ?? []).length === 0 ? (
                  <Typography fontSize={12} color="#94a3b8">No activity recorded yet.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {(p.logs as any[]).map((log) => (
                      <Paper key={log.id} elevation={0}
                        sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.25, display: "flex", gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip label={log.action.replace(/_/g, " ")} size="small"
                              sx={{
                                bgcolor:
                                  log.action === "created"            ? "#ecfdf5" :
                                  log.action === "credentials_sent"   ? "#eff6ff" :
                                  log.action === "password_reset"     ? "#fff7ed" :
                                  log.action === "deleted"            ? "#fef2f2" : "#f1f5f9",
                                color:
                                  log.action === "created"            ? "#065f46" :
                                  log.action === "credentials_sent"   ? "#1d4ed8" :
                                  log.action === "password_reset"     ? "#c2410c" :
                                  log.action === "deleted"            ? "#991b1b" : "#475569",
                                fontWeight: 600, fontSize: 10, textTransform: "capitalize",
                              }} />
                            <Typography fontSize={11} color="#94a3b8">
                              {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                            </Typography>
                          </Box>
                          {log.description && (
                            <Typography fontSize={12} color="#0f172a" mt={0.4}>{log.description}</Typography>
                          )}
                          <Typography fontSize={11} color="#94a3b8" mt={0.25}>
                            by {log.actor_username || "system"}{log.ip_address ? ` · ${log.ip_address}` : ""}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Login info email dialog */}
      <Dialog open={!!loginInfoDialog} onClose={() => setLoginInfoDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#16a34a" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontWeight={700} fontSize={15}>Send Login Info</Typography>
          <IconButton size="small" onClick={() => setLoginInfoDialog(null)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {loginInfoDialog && (
            <>
              <TextField label="To" size="small" fullWidth value={loginInfoDialog.toEmail}
                onChange={(e) => setLoginInfoDialog({ ...loginInfoDialog, toEmail: e.target.value })} />
              <TextField label="Subject" size="small" fullWidth value={loginInfoDialog.subject}
                onChange={(e) => setLoginInfoDialog({ ...loginInfoDialog, subject: e.target.value })} />
              <TextField label="Message" size="small" fullWidth multiline minRows={8}
                value={loginInfoDialog.message}
                onChange={(e) => setLoginInfoDialog({ ...loginInfoDialog, message: e.target.value })} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setLoginInfoDialog(null)}
            sx={{ textTransform: "none", borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitLoginInfo} disabled={sendingInfo}
            startIcon={!sendingInfo ? <ForwardToInboxOutlinedIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, minWidth: 140 }}>
            {sendingInfo ? <CircularProgress size={18} color="inherit" /> : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password confirmation */}
      <Dialog open={resendConfirmOpen} onClose={() => setResendConfirmOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>Reset password?</DialogTitle>
        <DialogContent>
          <Typography fontSize={13} color="text.secondary">
            A new password will be generated for{" "}
            <strong>{p?.full_name}</strong> and emailed to{" "}
            <strong>{p?.email}</strong>. Their current password will stop working immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResendConfirmOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleResendCredentials} disabled={resetting}
            startIcon={!resetting ? <AutoFixHighOutlinedIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#c2410c", "&:hover": { bgcolor: "#9a3412" } }}>
            {resetting ? <CircularProgress size={18} color="inherit" /> : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset success with password */}
      <Dialog open={!!successInfo} onClose={() => setSuccessInfo(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#2f3891" }} />
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>Password Reset</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper elevation={0}
            sx={{ bgcolor: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 2, p: 2, display: "flex", gap: 1.5 }}>
            <CheckCircleOutlineIcon sx={{ color: "#059669", fontSize: 22, mt: 0.2 }} />
            <Box>
              <Typography fontWeight={700} fontSize={13} color="#065f46">New password generated</Typography>
              <Typography fontSize={13} color="#065f46" mt={0.3}>
                <strong>{successInfo?.email}</strong> — copy or resend the credentials below.
              </Typography>
            </Box>
          </Paper>

          <Box>
            <Typography fontSize={11} fontWeight={700} color="#64748b" letterSpacing={0.5} mb={0.5}>GENERATED PASSWORD</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" fullWidth value={successInfo?.password ?? ""}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: "monospace", fontWeight: 600, letterSpacing: 0.5 },
                }} />
              <Tooltip title="Copy password">
                <IconButton onClick={handleCopyPassword}
                  sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, color: "#2f3891" }}>
                  <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography fontSize={11} color="#94a3b8" mt={0.75}>
              Save this password — it will not be shown again.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setSuccessInfo(null)} sx={{ textTransform: "none", color: "#64748b" }}>
            Close
          </Button>
          <Button variant="contained" onClick={handleSendCredentials} disabled={sending}
            startIcon={!sending ? <MailOutlineIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{ textTransform: "none", borderRadius: 1.5, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, minWidth: 160 }}>
            {sending ? <CircularProgress size={18} color="inherit" /> : "Send via Email"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfilePage;
